import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PreviewIcon from '@mui/icons-material/Preview';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import ProveedorSearch from '../../components/ProveedorSearch';
import SearchableSelect from '../../components/SearchableSelect';
import { Proveedor } from '../../services/proveedorService';
import {
  importarPreciosExcel,
  PreciosProveedorImportResult,
} from '../../services/preciosProveedorService';
import InformesBackButton from '../../components/shared/InformesBackButton';
import {
  EXCEL_IMPORT_SIZE_HELPER_TEXT,
  getExcelFileSizeStatusText,
  getExcelFileTooLargeMessage,
  isExcelImportTooLarge,
  resolveImportExcelError,
} from '../../utils/importExcelLimits';
import PreciosImportPreviewDialog from './PreciosImportPreviewDialog';
import {
  buildPreciosImportPreviewProveedor,
  PreciosImportPreviewRow,
} from '../../utils/buildPreciosImportPreview';
import {
  applyPreviewEditsToWorkbook,
  clonarWorkbook,
  parseExcelPreciosImportRows,
  workbookToXlsxFile,
} from '../../utils/preciosImportExcelParse';
import {
  buildPreviewColumnDefsProveedor,
  columnDefsToExcelColumnMap,
} from '../../utils/preciosImportPreviewColumns';

const ImportacionPreciosProveedor: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [codigoProveedor, setCodigoProveedor] = useState('');
  const [proveedorNombre, setProveedorNombre] = useState('');
  const [openProveedorSearch, setOpenProveedorSearch] = useState(false);
  const [hojasDisponibles, setHojasDisponibles] = useState<string[]>([]);
  const [nombreHoja, setNombreHoja] = useState('');
  const [primeraFilaDatos, setPrimeraFilaDatos] = useState('2');
  const [ultimaFilaDatos, setUltimaFilaDatos] = useState('');
  const [colCodigo, setColCodigo] = useState('B');
  const [colCosto, setColCosto] = useState('G');
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<PreciosProveedorImportResult | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFilas, setPreviewFilas] = useState<PreciosImportPreviewRow[]>([]);
  const [previewSubtitulo, setPreviewSubtitulo] = useState('');
  const workbookOriginalRef = useRef<XLSX.WorkBook | null>(null);

  const archivoExcedeLimite = isExcelImportTooLarge(archivo);

  const previewColumnas = useMemo(
    () => buildPreviewColumnDefsProveedor({ columnaCosto: colCosto }),
    [colCosto]
  );

  const invalidarWorkbook = () => {
    workbookOriginalRef.current = null;
  };

  const crearArchivoConEdicionesPreview = (): File | null => {
    const wbOriginal = workbookOriginalRef.current;
    if (!wbOriginal || !archivo) return null;
    const hoja = nombreHoja.trim();
    if (!hoja) return null;

    const wb = clonarWorkbook(wbOriginal);
    applyPreviewEditsToWorkbook(
      wb,
      {
        nombreHoja: hoja,
        ...columnDefsToExcelColumnMap(previewColumnas),
      },
      previewFilas
    );
    const baseName = archivo.name.replace(/\.(xlsx|xlsm)$/i, '');
    return workbookToXlsxFile(wb, `${baseName}-editado.xlsx`);
  };

  const armarPreview = async (
    wb: XLSX.WorkBook,
    res: PreciosProveedorImportResult,
    soloSimular: boolean,
    prov: number,
    opciones?: { preguntarAntesDeAbrir?: boolean }
  ) => {
    const hoja = nombreHoja.trim();
    const colCod = colCodigo.trim();
    const colCost = colCosto.trim();
    const primera = Math.max(1, parseIntOr(primeraFilaDatos, 1));
    const ultimaTrim = ultimaFilaDatos.trim();
    const ultima =
      ultimaTrim !== ''
        ? parseIntOr(ultimaTrim, NaN)
        : undefined;

    const filasExcel = parseExcelPreciosImportRows({
      workbook: wb,
      nombreHoja: hoja,
      primeraFilaDatos: primera,
      filasLeidasApi: res.filasLeidas,
      ...(ultima !== undefined && Number.isFinite(ultima) ? { ultimaFilaDatos: ultima } : {}),
      columnaCodigo: colCod,
      columnaCosto: colCost,
    });
    const preview = await buildPreciosImportPreviewProveedor(filasExcel, res);
    setPreviewFilas(preview);
    setPreviewSubtitulo(
      soloSimular
        ? `Simulación — proveedor ${prov} (puede editar costos antes de procesar)`
        : `Proceso finalizado — proveedor ${prov}`
    );
    if (soloSimular) {
      if (opciones?.preguntarAntesDeAbrir) {
        if (window.confirm('¿Desea abrir la previsualización de los cambios?')) {
          setPreviewOpen(true);
        }
      } else {
        setPreviewOpen(true);
      }
    } else {
      setPreviewOpen(false);
    }
  };

  const parseIntOr = (s: string, fallback: number) => {
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  const handleProveedorSelect = (proveedor: Proveedor) => {
    setCodigoProveedor(String(proveedor.nroProveedor));
    setProveedorNombre((proveedor.nombre ?? '').trim());
  };

  const handleProveedorNumeroChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const soloDigitos = e.target.value.replace(/\D/g, '');
    setCodigoProveedor(soloDigitos);
    setProveedorNombre('');
  };

  const ejecutar = async (
    soloSimular: boolean,
    archivoOverride?: File,
    opciones?: { preguntarAbrirPreview?: boolean }
  ) => {
    const archivoAUsar = archivoOverride ?? archivo;
    if (!archivoAUsar) {
      enqueueSnackbar('Seleccione un archivo Excel (.xlsx).', { variant: 'warning' });
      return;
    }
    const hoja = nombreHoja.trim();
    if (!hoja) {
      enqueueSnackbar('Seleccione la hoja del libro.', { variant: 'warning' });
      return;
    }
    const prov = parseIntOr(codigoProveedor, NaN);
    if (!Number.isFinite(prov) || prov <= 0) {
      enqueueSnackbar('Ingrese un número de proveedor válido.', { variant: 'warning' });
      return;
    }
    if (isExcelImportTooLarge(archivoAUsar)) {
      enqueueSnackbar(getExcelFileTooLargeMessage(archivoAUsar), { variant: 'error' });
      return;
    }

    setLoading(true);
    setResultado(null);
    try {
      const colCod = colCodigo.trim();
      const colCost = colCosto.trim();
      if (!colCod || !colCost) {
        enqueueSnackbar('Indique la columna del código de artículo del proveedor y la del costo (ej. B y G).', {
          variant: 'warning',
        });
        setLoading(false);
        return;
      }
      const primera = Math.max(1, parseIntOr(primeraFilaDatos, 1));
      const ultimaTrim = ultimaFilaDatos.trim();
      let ultima: number | undefined;
      if (ultimaTrim !== '') {
        ultima = parseIntOr(ultimaTrim, NaN);
        if (!Number.isFinite(ultima) || ultima < 1) {
          enqueueSnackbar('"Hasta fila" debe ser un número entero ≥ 1 o dejarse vacío.', { variant: 'warning' });
          setLoading(false);
          return;
        }
        if (ultima < primera) {
          enqueueSnackbar('"Hasta fila" no puede ser menor que la primera fila de datos.', { variant: 'warning' });
          setLoading(false);
          return;
        }
      }
      const res = await importarPreciosExcel(archivoAUsar, {
        codigoProveedor: prov,
        nombreHoja: hoja,
        primeraFilaDatos: primera,
        ...(ultima !== undefined ? { ultimaFilaDatos: ultima } : {}),
        columnaCodigoArticuloProveedor: colCod,
        columnaCosto: colCost,
        dryRun: soloSimular,
        guardarHistorico: !soloSimular,
      });
      setResultado(res);

      try {
        let wb: XLSX.WorkBook;
        if (archivoOverride) {
          const ab = await archivoOverride.arrayBuffer();
          wb = XLSX.read(ab, { type: 'array' });
        } else {
          let wbCached = workbookOriginalRef.current;
          if (!wbCached) {
            const ab = await archivoAUsar.arrayBuffer();
            wbCached = XLSX.read(ab, { type: 'array' });
            workbookOriginalRef.current = wbCached;
          }
          wb = wbCached;
        }
        await armarPreview(wb, res, soloSimular, prov, {
          preguntarAntesDeAbrir: opciones?.preguntarAbrirPreview ?? false,
        });
      } catch (previewErr) {
        console.error('Error al armar previsualización de precios', previewErr);
        enqueueSnackbar(
          'La importación se procesó, pero no se pudo armar la previsualización detallada.',
          { variant: 'warning' }
        );
      }

      if (res.advertencias.length > 0) {
        res.advertencias.forEach((a) => enqueueSnackbar(a, { variant: 'info' }));
      }
      enqueueSnackbar(
        soloSimular
          ? `Simulación: ${res.costosPorProveedorActualizados} CPP / ${res.stockRecalculados} stock.`
          : `Proceso finalizado: ${res.costosPorProveedorActualizados} CPP actualizados.`,
        { variant: 'success' }
      );
    } catch (e: unknown) {
      const msg = resolveImportExcelError(e, archivoAUsar, 'Error al importar precios.');
      console.error('Importación costos Base por código de proveedor', e);
      enqueueSnackbar(msg.length > 800 ? `${msg.slice(0, 800)}…` : msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const reSimularDesdePreview = async () => {
    const archivoEditado = crearArchivoConEdicionesPreview();
    if (!archivoEditado) {
      enqueueSnackbar('No hay archivo cargado para re-simular.', { variant: 'warning' });
      return;
    }
    await ejecutar(true, archivoEditado);
  };

  const procesarDesdePreview = async () => {
    const archivoEditado = crearArchivoConEdicionesPreview();
    if (!archivoEditado) {
      enqueueSnackbar('No hay archivo cargado para procesar.', { variant: 'warning' });
      return;
    }
    await ejecutar(false, archivoEditado);
  };

  const cardSx = {
    bgcolor: '#ffffff',
    borderRadius: 3,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden' as const,
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Stack spacing={2}>
        <Card sx={cardSx}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <InformesBackButton to="/inventario/mantenimiento-precios" useBrowserBack />
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Actualización de Costos Base por código de proveedor desde Excel
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Busca por <strong>Codigo de Articulo del Proveedor</strong> en el excel y actualiza el <strong>Costo Base</strong> de cada producto, actualizando tambien su <strong>Precio de Venta Base</strong>
              </Typography>

              <TextField
                fullWidth
                required
                label="Nº proveedor"
                value={codigoProveedor}
                onChange={handleProveedorNumeroChange}
                inputProps={{ inputMode: 'numeric', 'aria-label': 'Número de proveedor' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenProveedorSearch(true)}
                        edge="end"
                        size="small"
                        aria-label="Buscar proveedor"
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={
                  proveedorNombre
                    ? `Nombre: ${proveedorNombre}`
                    : 'Ingrese el número manualmente o use la lupa para buscar por nombre u otros datos.'
                }
              />

              <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
                {EXCEL_IMPORT_SIZE_HELPER_TEXT}
              </Alert>

              <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                {archivo ? archivo.name : 'Elegir archivo .xlsx'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xlsm"
                  onChange={async (e) => {
                    const input = e.target;
                    const f = input.files?.[0] ?? null;
                    setArchivo(f);
                    setHojasDisponibles([]);
                    setNombreHoja('');
                    setResultado(null);
                    invalidarWorkbook();
                    if (!f) {
                      input.value = '';
                      return;
                    }
                    if (isExcelImportTooLarge(f)) {
                      enqueueSnackbar(getExcelFileTooLargeMessage(f), { variant: 'error' });
                      input.value = '';
                      return;
                    }
                    try {
                      const ab = await f.arrayBuffer();
                      const wb = XLSX.read(ab, { type: 'array' });
                      workbookOriginalRef.current = wb;
                      const names = wb.SheetNames ?? [];
                      if (names.length === 0) {
                        enqueueSnackbar('El archivo no contiene hojas reconocibles.', { variant: 'warning' });
                        setArchivo(null);
                        input.value = '';
                        return;
                      }
                      setHojasDisponibles(names);
                      setNombreHoja(names[0]);
                    } catch (err) {
                      console.error('Lectura de hojas Excel', err);
                      enqueueSnackbar(
                        'No se pudo leer el archivo Excel. Verifique que sea .xlsx o .xlsm válido.',
                        { variant: 'error' }
                      );
                      setArchivo(null);
                      input.value = '';
                    }
                    input.value = '';
                  }}
                />
              </Button>
              {archivo && (
                <Typography variant="body2" color={archivoExcedeLimite ? 'error' : 'text.secondary'}>
                  {getExcelFileSizeStatusText(archivo)}
                </Typography>
              )}
              {archivoExcedeLimite && archivo && (
                <Alert severity="error">{getExcelFileTooLargeMessage(archivo)}</Alert>
              )}

              <SearchableSelect
                label="Hoja"
                disabled={hojasDisponibles.length === 0 || archivoExcedeLimite}
                options={hojasDisponibles.map((n) => ({ value: n, label: n }))}
                value={hojasDisponibles.includes(nombreHoja) ? nombreHoja : null}
                onChange={(value) => setNombreHoja(value ?? '')}
                helperText={
                  hojasDisponibles.length === 0
                    ? 'Elija un archivo para listar las hojas del libro.'
                    : 'Hojas detectadas en el archivo seleccionado.'
                }
              />
              <TextField
                label="Número de la primera fila de datos"
                value={primeraFilaDatos}
                onChange={(e) => setPrimeraFilaDatos(e.target.value)}
                type="number"
                fullWidth
              />
              <TextField
                label="Hasta fila (última fila Excel a procesar)"
                value={ultimaFilaDatos}
                onChange={(e) => setUltimaFilaDatos(e.target.value)}
                type="number"
                fullWidth
                helperText="Opcional. Vacío = hasta el final del archivo. Inclusive (misma numeración que Excel)."
              />
              <TextField
                label="Letra de la columna código artículo proveedor"
                value={colCodigo}
                onChange={(e) => setColCodigo(e.target.value.toUpperCase())}
                fullWidth
                placeholder="Ej: B o AB (columna Excel)"
              />
              <TextField
                label="Letra de la columna costo"
                value={colCosto}
                onChange={(e) => setColCosto(e.target.value.toUpperCase())}
                fullWidth
                placeholder="Ej: G (letra de columna Excel)"
              />

              <FormControlLabel
                control={<Checkbox checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
                label="Solo simular: no realiza cambios en la base de datos"
              />
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label="Guardar snapshot en CostosPorProveedorHistorico antes de realizar cambios"
              />

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PreviewIcon />}
                  disabled={loading || archivoExcedeLimite}
                  onClick={() => ejecutar(true, undefined, { preguntarAbrirPreview: true })}
                >
                  Simular
                </Button>
                <Button
                  variant="contained"
                  disabled={loading || dryRun || archivoExcedeLimite}
                  onClick={() => ejecutar(false)}
                >
                  Procesar
                </Button>
              </Stack>
              {dryRun && (
                <Typography variant="caption" color="text.secondary">
                  Desactive &quot;Solo simular&quot; para habilitar Procesar.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        {resultado && (
          <Card sx={cardSx}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  Resultado {resultado.dryRun ? '(simulación)' : ''}
                </Typography>
                {previewFilas.length > 0 && (
                  <Button variant="outlined" size="small" startIcon={<PreviewIcon />} onClick={() => setPreviewOpen(true)}>
                    {resultado.dryRun ? 'Ver previsualización' : 'Ver detalle de cambios aplicados'}
                  </Button>
                )}
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body2">Filas leídas: {resultado.filasLeidas}</Typography>
                <Typography variant="body2">
                  CPP actualizados: {resultado.costosPorProveedorActualizados}
                </Typography>
                {(resultado.costosPorProveedorCreadosDesdeStock ?? 0) > 0 && (
                  <Typography variant="body2">
                    CPP nuevos (desde Stock): {resultado.costosPorProveedorCreadosDesdeStock}
                  </Typography>
                )}
                <Typography variant="body2">
                  Costos Base de Articulos actualizados: {resultado.stockRecalculados}
                </Typography>
                <Typography variant="body2">
                  Código de Articulo del Proveedor no encontrado: {resultado.sinCoincidenciaCostosPorProveedor}
                </Typography>
                <Typography variant="body2">Filas inválidas: {resultado.filasInvalidas}</Typography>
              </Stack>
              {resultado.erroresFila.length > 0 && (
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {resultado.erroresFila.slice(0, 50).map((e) => (
                    <li key={e}>
                      <Typography variant="caption">{e}</Typography>
                    </li>
                  ))}
                  {resultado.erroresFila.length > 50 && (
                    <Typography variant="caption">… y más</Typography>
                  )}
                </Box>
              )}
              {resultado.sinCoincidenciaDetalle.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Primeras filas sin CPP ({Math.min(20, resultado.sinCoincidenciaDetalle.length)} de{' '}
                    {resultado.sinCoincidenciaDetalle.length}):
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    {resultado.sinCoincidenciaDetalle.slice(0, 20).map((d) => (
                      <li key={`${d.filaExcel}-${d.codigoArticuloProveedor}`}>
                        <Typography variant="caption">
                          Fila {d.filaExcel}: {d.codigoArticuloProveedor}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Stack>

      <ProveedorSearch
        open={openProveedorSearch}
        onClose={() => setOpenProveedorSearch(false)}
        onSelect={handleProveedorSelect}
      />

      <PreciosImportPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        filas={previewFilas}
        columnas={previewColumnas}
        onFilasChange={setPreviewFilas}
        subtitulo={previewSubtitulo}
        editable={!!resultado?.dryRun}
        procesado={resultado?.dryRun === false}
        puedeEjecutarDesdePreview={!!resultado?.dryRun}
        onReSimular={reSimularDesdePreview}
        onProcesar={procesarDesdePreview}
        ejecutando={loading}
      />
    </Box>
  );
};

export default ImportacionPreciosProveedor;
